<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import MarkdownIt from 'markdown-it';
import { useBranding } from 'shared/composables/useBranding';
import HelpManualAPI from 'dashboard/api/helpCenter/helpManual';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import ArticleFeedbackActions from 'shared/components/ArticleFeedbackActions.vue';
import { getContextualHelpConfig } from 'dashboard/constants/contextualHelp';

const props = defineProps({
  show: { type: Boolean, default: false },
  systemKey: { type: String, required: true },
  articleKey: { type: String, default: '' },
});

const emit = defineEmits(['close']);

const markdown = new MarkdownIt({ html: true, linkify: true });

const { locale, t } = useI18n();
const { replaceInstallationName } = useBranding();

const content = ref('');
const isLoading = ref(false);
const hasError = ref(false);
const isNotFound = ref(false);
const articleUrl = ref('');
const videoUrl = ref('');
const helpCenterUrl = ref('');
const resolvedTitle = ref('');
const contentElement = ref(null);
const portalSlug = ref('');
const articleSlug = ref('');
const resolvedArticleKey = ref('');
const showFeedbackReasons = ref(false);
const feedbackStatus = ref('');
const feedbackStatusType = ref('success');
const reportMessage = ref('');
const reportReason = ref('reported_error');
const isSubmittingFeedback = ref(false);
const reportDialog = ref(null);
const helpfulCount = ref(0);
const unhelpfulCount = ref(0);
const selectedReaction = ref('');
const confirmingReason = ref('');

const feedbackSelectionStorageKey = 'chatwoot_article_feedback_selections';
const hasSelectedReaction = computed(() => Boolean(selectedReaction.value));
const feedbackReasons = computed(() => [
  {
    key: 'not_resolved',
    label: t('CONTEXTUAL_HELP.FEEDBACK.REASONS.NOT_RESOLVED'),
  },
  {
    key: 'outdated',
    label: t('CONTEXTUAL_HELP.FEEDBACK.REASONS.OUTDATED'),
  },
  {
    key: 'hard_to_understand',
    label: t('CONTEXTUAL_HELP.FEEDBACK.REASONS.HARD_TO_UNDERSTAND'),
  },
]);

const feedbackSuccessMessage = (reaction, reason) => {
  if (reaction === 'helpful') {
    return t('CONTEXTUAL_HELP.FEEDBACK.THANKS_HELPFUL');
  }
  if (reason === 'reported_error') {
    return t('CONTEXTUAL_HELP.FEEDBACK.THANKS_REPORT');
  }
  return t('CONTEXTUAL_HELP.FEEDBACK.THANKS_UNHELPFUL');
};

const storedFeedbackSelections = () => {
  try {
    return JSON.parse(
      window.localStorage?.getItem(feedbackSelectionStorageKey) || '{}'
    );
  } catch {
    return {};
  }
};

const feedbackSelectionKey = () => `${portalSlug.value}:${articleSlug.value}`;

const restoreFeedbackSelection = () => {
  selectedReaction.value =
    storedFeedbackSelections()[feedbackSelectionKey()] || '';
};

const persistFeedbackSelection = reaction => {
  const selections = storedFeedbackSelections();
  selections[feedbackSelectionKey()] = reaction;
  window.localStorage?.setItem(
    feedbackSelectionStorageKey,
    JSON.stringify(selections)
  );
  selectedReaction.value = reaction;
};

const config = computed(() => getContextualHelpConfig(props.systemKey));
const title = computed(
  () =>
    resolvedTitle.value ||
    (config.value?.titleKey
      ? t(config.value.titleKey)
      : t('CONTEXTUAL_HELP.TITLE'))
);
const renderedContent = computed(() => markdown.render(content.value));
const youtubeVideoId = (url, hostname) => {
  if (hostname === 'youtu.be') {
    return url.pathname.split('/').filter(Boolean)[0];
  }

  if (['youtube.com', 'm.youtube.com'].includes(hostname)) {
    return (
      url.searchParams.get('v') || url.pathname.match(/^\/embed\/([^/]+)/)?.[1]
    );
  }

  return '';
};
const videoPresentation = computed(() => {
  if (!videoUrl.value) return null;

  try {
    const url = new URL(videoUrl.value);
    const hostname = url.hostname.replace(/^www\./, '');
    const youtubeId = youtubeVideoId(url, hostname);

    if (youtubeId) {
      return {
        type: 'youtube',
        url: `https://www.youtube-nocookie.com/embed/${youtubeId}`,
      };
    }

    if (/\.(mp4|webm|ogg)$/i.test(url.pathname)) {
      return { type: 'video', url: url.toString() };
    }

    return { type: 'link', url: url.toString() };
  } catch {
    return null;
  }
});
const fetchManualArticle = async () => {
  const articleKey = props.articleKey || config.value?.articleKey;
  if (!articleKey) return '';

  const { data: article } = await HelpManualAPI.getArticle(
    articleKey,
    locale.value
  );
  articleUrl.value = article.url || '';
  videoUrl.value = article.context?.video_url || '';
  portalSlug.value = article.portal_slug || '';
  articleSlug.value = article.article_slug || '';
  resolvedArticleKey.value = article.article_key || articleKey;
  helpfulCount.value = Number(article.feedback_counts?.helpful || 0);
  unhelpfulCount.value = Number(article.feedback_counts?.unhelpful || 0);
  restoreFeedbackSelection();
  resolvedTitle.value = replaceInstallationName(article.title || '');
  return article.content || '';
};

const scrollToAnchor = async () => {
  const anchor = config.value?.anchor;
  if (!anchor) return;

  await nextTick();
  contentElement.value
    ?.querySelector(`[id="${CSS.escape(anchor)}"]`)
    ?.scrollIntoView({ block: 'start' });
};

const openFullArticle = () => {
  window.open(articleUrl.value, '_blank', 'noopener,noreferrer');
};

const openHelpCenter = () => {
  window.open(helpCenterUrl.value, '_blank', 'noopener,noreferrer');
};

const openVideo = () => {
  if (!videoPresentation.value) return;

  window.open(videoPresentation.value.url, '_blank', 'noopener,noreferrer');
};

const feedbackVisitorId = () => {
  const storageKey = 'chatwoot_article_feedback_visitor_id';
  const existingId = window.localStorage?.getItem(storageKey);
  if (existingId) return existingId;

  const generatedId =
    window.crypto?.randomUUID?.() ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  window.localStorage?.setItem(storageKey, generatedId);
  return generatedId;
};

const submitFeedback = async ({ reaction, message = '', reason = '' }) => {
  if (
    !portalSlug.value ||
    !articleSlug.value ||
    isSubmittingFeedback.value ||
    hasSelectedReaction.value
  ) {
    return false;
  }

  isSubmittingFeedback.value = true;
  feedbackStatus.value = '';

  try {
    const { data } = await HelpManualAPI.submitArticleFeedback(
      portalSlug.value,
      articleSlug.value,
      {
        reaction,
        feedback_message: message,
        visitor_id: feedbackVisitorId(),
        metadata: {
          locale: locale.value,
          source: 'internal_help_drawer',
          article_key: resolvedArticleKey.value,
          article_title: resolvedTitle.value,
          page_url: window.location.href,
          ...(reason ? { reason } : {}),
        },
      }
    );
    helpfulCount.value = Number(data?.counts?.helpful ?? helpfulCount.value);
    unhelpfulCount.value = Number(
      data?.counts?.unhelpful ?? unhelpfulCount.value
    );
    persistFeedbackSelection(reaction);
    feedbackStatusType.value = 'success';
    feedbackStatus.value = feedbackSuccessMessage(reaction, reason);
    return true;
  } catch {
    feedbackStatusType.value = 'error';
    feedbackStatus.value = t('CONTEXTUAL_HELP.FEEDBACK.ERROR');
    return false;
  } finally {
    isSubmittingFeedback.value = false;
  }
};

const submitReason = async reason => {
  if (hasSelectedReaction.value) return;

  confirmingReason.value = reason.key;
  const wasSubmitted = await submitFeedback({
    reaction: 'unhelpful',
    message: reason.label,
    reason: reason.key,
  });
  if (wasSubmitted) {
    await new Promise(resolve => {
      setTimeout(resolve, 450);
    });
    showFeedbackReasons.value = false;
  }
  confirmingReason.value = '';
};

const openReportDialog = reason => {
  if (hasSelectedReaction.value) return;
  reportReason.value = reason;
  reportMessage.value = '';
  reportDialog.value?.open();
};

const submitReport = async () => {
  const wasSubmitted = await submitFeedback({
    reaction: 'unhelpful',
    message: reportMessage.value.trim(),
    reason: reportReason.value,
  });
  if (!wasSubmitted) return;

  reportDialog.value?.close();
  reportMessage.value = '';
  confirmingReason.value = reportReason.value;
  await new Promise(resolve => {
    setTimeout(resolve, 450);
  });
  showFeedbackReasons.value = false;
  confirmingReason.value = '';
};

const submitHelpfulFeedback = () => {
  if (hasSelectedReaction.value) return;
  submitFeedback({ reaction: 'helpful' });
};

const toggleFeedbackReasons = () => {
  if (hasSelectedReaction.value) return;
  showFeedbackReasons.value = !showFeedbackReasons.value;
};

const fetchHelpDocument = async () => {
  if (!props.show || !(props.articleKey || config.value?.articleKey)) return;

  isLoading.value = true;
  hasError.value = false;
  isNotFound.value = false;
  content.value = '';
  articleUrl.value = '';
  videoUrl.value = '';
  helpCenterUrl.value = '';
  resolvedTitle.value = '';
  portalSlug.value = '';
  articleSlug.value = '';
  resolvedArticleKey.value = '';
  showFeedbackReasons.value = false;
  feedbackStatus.value = '';
  reportMessage.value = '';
  helpfulCount.value = 0;
  unhelpfulCount.value = 0;
  selectedReaction.value = '';
  confirmingReason.value = '';

  try {
    content.value = replaceInstallationName(await fetchManualArticle());
  } catch (error) {
    isNotFound.value = error?.response?.status === 404;
    helpCenterUrl.value = error?.response?.data?.help_center_url || '';
    hasError.value = !isNotFound.value;
  }

  hasError.value ||= !content.value && !isNotFound.value;
  isLoading.value = false;
  await scrollToAnchor();
};

watch(
  () => [props.show, props.systemKey, props.articleKey, locale.value],
  fetchHelpDocument,
  { immediate: true }
);
</script>

<template>
  <Teleport to="body">
    <div
      v-if="show"
      class="fixed inset-0 z-50 flex justify-end bg-n-slate-12/30"
      role="presentation"
      @click.self="emit('close')"
    >
      <aside
        class="flex h-full w-full max-w-2xl flex-col border-l border-n-weak bg-n-surface-1 shadow-xl"
        role="dialog"
        aria-modal="true"
        :aria-label="title"
      >
        <header
          class="flex items-center justify-between gap-3 border-b border-n-weak px-5 py-4 sm:px-7"
        >
          <div class="min-w-0">
            <p class="text-xs font-medium uppercase text-n-slate-11">
              {{ $t('CONTEXTUAL_HELP.EYEBROW') }}
            </p>
            <h2 class="truncate text-base font-semibold text-n-slate-12">
              {{ title }}
            </h2>
          </div>
          <div class="flex shrink-0 items-center gap-1">
            <Button
              v-if="articleUrl"
              v-tooltip.bottom="$t('CONTEXTUAL_HELP.OPEN_FULL_ARTICLE')"
              icon="i-lucide-external-link"
              color="slate"
              size="sm"
              variant="ghost"
              :aria-label="$t('CONTEXTUAL_HELP.OPEN_FULL_ARTICLE')"
              @click="openFullArticle"
            />
            <Button
              v-tooltip.left="$t('GENERAL.CLOSE')"
              icon="i-lucide-x"
              color="slate"
              size="sm"
              variant="ghost"
              :aria-label="$t('GENERAL.CLOSE')"
              @click="emit('close')"
            />
          </div>
        </header>

        <div
          ref="contentElement"
          class="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-7 sm:py-6"
        >
          <div
            v-if="isLoading"
            class="flex h-40 items-center justify-center text-n-slate-11"
          >
            <span class="i-lucide-loader-2 size-5 animate-spin" />
          </div>
          <div
            v-else-if="isNotFound"
            class="flex min-h-64 flex-col items-center justify-center gap-3 rounded-lg border border-n-weak bg-n-alpha-1 p-6 text-center"
          >
            <span class="i-lucide-file-question size-8 text-n-slate-10" />
            <h3 class="text-base font-medium text-n-slate-12">
              {{ $t('CONTEXTUAL_HELP.NOT_FOUND_TITLE') }}
            </h3>
            <p class="max-w-md text-sm text-n-slate-11">
              {{ $t('CONTEXTUAL_HELP.NOT_FOUND_DESCRIPTION') }}
            </p>
            <Button
              v-if="helpCenterUrl"
              :label="$t('CONTEXTUAL_HELP.OPEN_HELP_CENTER')"
              icon="i-lucide-library"
              color="blue"
              variant="faded"
              @click="openHelpCenter"
            />
          </div>
          <div
            v-else-if="hasError"
            class="rounded-lg border border-n-weak bg-n-alpha-1 p-4 text-sm text-n-slate-11"
          >
            {{ $t('CONTEXTUAL_HELP.LOAD_ERROR') }}
          </div>
          <article
            v-else
            v-dompurify-html="renderedContent"
            data-testid="help-document"
            class="prose prose-sm dark:prose-invert max-w-none break-words text-start prose-headings:font-semibold prose-headings:text-n-slate-12 prose-p:font-normal prose-p:leading-6 prose-p:text-n-slate-11 prose-li:font-normal prose-li:text-n-slate-11 prose-a:font-medium prose-a:text-n-brand prose-a:underline prose-a:decoration-n-brand/40 prose-a:underline-offset-2 prose-strong:font-semibold prose-strong:text-n-slate-12 [&_blockquote]:rounded-e-lg [&_blockquote]:border-n-brand [&_blockquote]:bg-n-brand/5 [&_blockquote]:py-1 [&_code]:rounded [&_code]:bg-n-alpha-2 [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs [&_code]:before:content-none [&_code]:after:content-none [&_h1]:text-2xl [&_h2]:text-xl [&_h3]:text-lg [&_h4]:border-b [&_h4]:border-n-weak [&_h4]:pb-2 [&_h4]:text-base [&_img]:rounded-lg [&_li>p]:m-0 [&_pre]:rounded-lg [&_pre]:bg-n-slate-2 [&_table]:overflow-hidden [&_table]:rounded-lg [&_th]:bg-n-slate-2 [&_th]:font-semibold [&_th]:text-n-slate-12 [&_td]:text-n-slate-11"
          />
          <section
            v-if="videoPresentation"
            class="mt-8 border-t border-n-weak pt-6"
          >
            <h3 class="mb-3 text-base font-semibold text-n-slate-12">
              {{ $t('CONTEXTUAL_HELP.VIDEO_TITLE') }}
            </h3>
            <div
              v-if="videoPresentation.type === 'youtube'"
              class="aspect-video overflow-hidden rounded-lg border border-n-weak bg-n-slate-2"
            >
              <iframe
                class="size-full"
                :src="videoPresentation.url"
                :title="title"
                loading="lazy"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              />
            </div>
            <video
              v-else-if="videoPresentation.type === 'video'"
              class="aspect-video w-full rounded-lg border border-n-weak bg-n-slate-2"
              :src="videoPresentation.url"
              controls
              preload="metadata"
            />
            <Button
              v-else
              :label="$t('CONTEXTUAL_HELP.OPEN_VIDEO')"
              icon="i-lucide-play"
              color="blue"
              variant="faded"
              @click="openVideo"
            />
          </section>
          <section
            v-if="content && portalSlug && articleSlug"
            class="mt-8 rounded-xl border border-n-weak bg-n-alpha-1 p-4"
            data-testid="help-feedback"
          >
            <ArticleFeedbackActions
              :question="$t('CONTEXTUAL_HELP.FEEDBACK.QUESTION')"
              :helpful-label="$t('CONTEXTUAL_HELP.FEEDBACK.HELPFUL')"
              :unhelpful-label="$t('CONTEXTUAL_HELP.FEEDBACK.NOT_HELPFUL')"
              :report-label="$t('CONTEXTUAL_HELP.FEEDBACK.REPORT_ERROR')"
              :helpful-count="helpfulCount"
              :unhelpful-count="unhelpfulCount"
              :selected-reaction="selectedReaction"
              :submitting="isSubmittingFeedback"
              :reasons-expanded="showFeedbackReasons"
              test-id-prefix="help-feedback"
              @helpful="submitHelpfulFeedback"
              @unhelpful="toggleFeedbackReasons"
              @report="openReportDialog('reported_error')"
            />
            <div
              v-if="showFeedbackReasons"
              class="mt-4 border-t border-n-weak pt-4"
              data-testid="help-feedback-reasons"
            >
              <p class="mb-2 text-sm font-medium text-n-slate-12">
                {{ $t('CONTEXTUAL_HELP.FEEDBACK.REASONS_TITLE') }}
              </p>
              <div class="flex flex-wrap gap-2">
                <Button
                  v-for="reason in feedbackReasons"
                  :key="reason.key"
                  :data-testid="`help-feedback-reason-${reason.key}`"
                  :label="reason.label"
                  :color="confirmingReason === reason.key ? 'ruby' : 'slate'"
                  :variant="confirmingReason === reason.key ? 'solid' : 'faded'"
                  :disabled="isSubmittingFeedback"
                  class="min-h-10 flex-1 sm:min-w-56"
                  justify="start"
                  @click="submitReason(reason)"
                />
                <Button
                  data-testid="help-feedback-reason-other"
                  :label="$t('CONTEXTUAL_HELP.FEEDBACK.REASONS.OTHER')"
                  :color="confirmingReason === 'other' ? 'ruby' : 'slate'"
                  :variant="confirmingReason === 'other' ? 'solid' : 'faded'"
                  class="min-h-10 flex-1 sm:min-w-56"
                  justify="start"
                  :disabled="isSubmittingFeedback"
                  @click="openReportDialog('other')"
                />
              </div>
            </div>
            <p
              v-if="feedbackStatus"
              class="mb-0 mt-3 text-sm"
              :class="
                feedbackStatusType === 'error'
                  ? 'text-n-ruby-11'
                  : 'text-n-teal-11'
              "
              role="status"
            >
              {{ feedbackStatus }}
            </p>
          </section>
        </div>
      </aside>
    </div>
    <Dialog
      ref="reportDialog"
      :title="$t('CONTEXTUAL_HELP.FEEDBACK.MODAL_TITLE')"
      :description="$t('CONTEXTUAL_HELP.FEEDBACK.MODAL_DESCRIPTION')"
      :cancel-button-label="$t('CONTEXTUAL_HELP.FEEDBACK.CANCEL')"
      :confirm-button-label="$t('CONTEXTUAL_HELP.FEEDBACK.SUBMIT')"
      :disable-confirm-button="!reportMessage.trim()"
      :is-loading="isSubmittingFeedback"
      width="md"
      @confirm="submitReport"
    >
      <TextArea
        id="help-feedback-report-message"
        v-model="reportMessage"
        :label="$t('CONTEXTUAL_HELP.FEEDBACK.MODAL_LABEL')"
        :placeholder="$t('CONTEXTUAL_HELP.FEEDBACK.MODAL_PLACEHOLDER')"
        :max-length="1000"
        show-character-count
        auto-height
        autofocus
      />
    </Dialog>
  </Teleport>
</template>
